import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SchoolLayout from '../../components/SchoolLayout';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Save, User } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    ic_masked: '',
    dob: '',
    gender: 'Lelaki',
    phone: '',
    address: '',
    school_id: user?.school_id || ''
  });

  useEffect(() => {
    if (id) {
      fetchMember();
    }
  }, [id]);

  const fetchMember = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/members/${id}`);
      const member = response.data;
      setFormData({
        full_name: member.full_name,
        ic_masked: member.ic_masked,
        dob: member.dob,
        gender: member.gender,
        phone: member.phone,
        address: member.address,
        school_id: member.school_id
      });
    } catch (error) {
      console.error('Failed to fetch member:', error);
      toast.error('Gagal memuatkan maklumat ahli');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (id) {
        await axios.put(`${API_URL}/members/${id}`, formData);
        toast.success('Maklumat ahli berjaya dikemaskini');
      } else {
        await axios.post(`${API_URL}/members`, formData);
        toast.success('Ahli berjaya didaftarkan');
      }
      navigate('/school/members');
    } catch (error) {
      console.error('Failed to save member:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan maklumat ahli');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SchoolLayout>
        <div className="text-center py-12">
          <div className="animate-pulse text-[#D71920]">Memuatkan...</div>
        </div>
      </SchoolLayout>
    );
  }

  return (
    <SchoolLayout>
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/school/members')}
            className="border-[#E5E5E5]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali
          </Button>
        </div>

        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 rounded-full bg-[#D71920]/10 flex items-center justify-center">
            <User className="w-8 h-8 text-[#D71920]" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]" >
              {id ? 'Kemaskini Ahli' : 'Daftar Ahli Baru'}
            </h1>
            <p className="text-[#555]">Isi maklumat ahli dengan lengkap</p>
          </div>
        </div>

        <div ></div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-[#E5E5E5] p-8 space-y-6" data-testid="member-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label htmlFor="full_name">Nama Penuh *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                
                className="border-[#E5E5E5]"
                data-testid="input-name"
              />
            </div>

            <div>
              <Label htmlFor="ic_masked">Nombor IC (Tertutup) *</Label>
              <Input
                id="ic_masked"
                value={formData.ic_masked}
                onChange={(e) => setFormData({ ...formData, ic_masked: e.target.value })}
                required
                
                className="border-[#E5E5E5]"
                data-testid="input-ic"
              />
              <p className="text-xs text-[#777] mt-1">
              </p>
            </div>

            <div>
              <Label htmlFor="dob">Tarikh Lahir *</Label>
              <Input
                id="dob"
                type="date"
                value={formData.dob}
                onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                required
                className="border-[#E5E5E5]"
                data-testid="input-dob"
              />
            </div>

            <div>
              <Label htmlFor="gender">Jantina *</Label>
              <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                <SelectTrigger className="border-[#E5E5E5]" data-testid="input-gender">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Lelaki">Lelaki</SelectItem>
                  <SelectItem value="Perempuan">Perempuan</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="phone">Telefon *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                
                className="border-[#E5E5E5]"
                data-testid="input-phone"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="address">Alamat *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                
                className="border-[#E5E5E5]"
                data-testid="input-address"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E5E5]">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/school/members')}
              className="border-[#E5E5E5]"
              data-testid="cancel-button"
            >
              Batal
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#D71920] hover:bg-[#b8161c] text-white px-8"
              data-testid="save-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Menyimpan...' : id ? 'Simpan Perubahan' : 'Daftar Ahli'}
            </Button>
          </div>
        </form>
      </div>
    </SchoolLayout>
  );
};

export default MemberForm;
